import "@/App.css";
import "@/tema-escuro.css";
import "@/lib/axios";
import { API_BASE } from "@/lib/api";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { 
  LayoutDashboard, 
  Package, 
  FileSpreadsheet, 
  BarChart3, 
  Settings,
  Download,
  Menu,
  X,
  ClipboardList,
  GitCompare,
  LogOut,
  Moon,
  Sun,
  User,
  UserCog,
  ShieldCheck,
  MailWarning,
  MessageCircle
} from "lucide-react";
import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Remove platform badge
(function hideBadge() {
  const rm = () => { const b = document.getElementById("emergent-badge"); if (b) b.remove(); };
  rm();
  new MutationObserver(rm).observe(document.body, { childList: true, subtree: true });
})();

// Pages
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import CostSheets from "@/pages/CostSheets";
import CostSheetForm from "@/pages/CostSheetForm";
import Reports from "@/pages/Reports";
import ConfigPage from "@/pages/Config";
import TechnicalSheets from "@/pages/TechnicalSheets";
import TechnicalSheetForm from "@/pages/TechnicalSheetForm";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CheckoutRedirect from "@/pages/CheckoutRedirect";
import ObrigadoPage from "@/pages/ObrigadoPage";
import Comparison from "@/pages/Comparison";
import Templates, { TemplateForm } from "@/pages/Templates";
import Landing from "@/pages/Landing";
import AccountPage, { SubscriptionPage } from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import { TermosPage, PrivacidadePage } from "@/pages/LegalPages";
import { RedefinirSenhaPage, ConfirmarEmailPage, ConvitePage } from "@/pages/AuthLinkPages";
import { useSuporte, linkWhatsApp } from "@/lib/suporte";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/products", icon: Package, label: "Produtos/Insumos" },
  { path: "/technical-sheets", icon: ClipboardList, label: "Fichas Tecnicas" },
  { path: "/cost-sheets", icon: FileSpreadsheet, label: "Precificacao" },
  { path: "/comparison", icon: GitCompare, label: "Comparativo" },
  { path: "/reports", icon: BarChart3, label: "Relatorios" },
  { path: "/config", icon: Settings, label: "Configuracoes" },
  { path: "/conta", icon: UserCog, label: "Minha conta" },
];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  // Admin sem 2FA so acessa a tela de seguranca; assinatura vencida vai para renovar.
  if ((user?.precisa_trocar_senha || user?.precisa_2fa) && location.pathname !== "/conta") return <Navigate to="/conta?aba=seguranca" replace />;
  if (user && !user.tem_acesso && location.pathname !== "/conta") return <Navigate to="/assinatura" replace />;
  return children;
}

function Sidebar({ isOpen, onClose, darkMode, setDarkMode }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const suporte = useSuporte(!!user);
  
  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: 'Manrope' }}>
                  Aesthetica
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">Pricing Pro</p>
              </div>
              <button onClick={onClose} className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>
          
          {/* User info */}
          {user && (
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate" data-testid="sidebar-user-name">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate" data-testid="sidebar-user-email">{user.email}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {[...navItems, ...(user?.role === "admin" ? [{ path: "/admin", icon: ShieldCheck, label: "Admin" }] : [])].map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) => `
                  sidebar-item
                  ${isActive || (item.path === "/" && location.pathname === "/") ? 'active' : ''}
                `}
                end={item.path === "/"}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          
          {/* Bottom section */}
          <div className="p-4 border-t border-slate-100 dark:border-slate-800 space-y-2">
            {suporte && (
              <a href={linkWhatsApp(suporte, user)} target="_blank" rel="noreferrer" className="sidebar-item w-full"
                title="Atendimento em dias úteis" data-testid="suporte-whatsapp">
                <MessageCircle className="w-5 h-5" />
                <span>Suporte no WhatsApp</span>
              </a>
            )}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="sidebar-item w-full"
              data-testid="dark-mode-toggle"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              <span>{darkMode ? "Modo Claro" : "Modo Escuro"}</span>
            </button>

            <a
              href={`${API_BASE}/api/export/backup`}
              download
              className="sidebar-item w-full justify-center bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
              data-testid="export-backup-btn"
            >
              <Download className="w-5 h-5" />
              <span>Exportar Backup</span>
            </a>

            <button
              onClick={logout}
              className="sidebar-item w-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function AvisoEmail() {
  const { user } = useAuth();
  if (!user || user.email_verificado || user.role === "admin") return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
      <MailWarning className="h-4 w-4" />
      Confirme seu e-mail ({user.email}) para conseguir recuperar a senha.
      <NavLink to="/conta?aba=seguranca" className="font-medium underline">Reenviar o link</NavLink>
    </div>
  );
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  
  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
    // O tema escuro vale so dentro do app. Sem isto, ao sair a pagina de vendas
    // e o login herdavam o escuro e ficavam com o texto apagado.
    return () => document.documentElement.classList.remove("dark");
  }, [darkMode]);
  
  return (
    <div className="app-container bg-slate-50 dark:bg-slate-950 min-h-screen">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
      
      <main className="main-content flex-1 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
            <h1 className="text-lg font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: 'Manrope' }}>
              Aesthetica
            </h1>
            <div className="w-9" />
          </div>
        </header>
        
        <AvisoEmail />
        <div className="flex-1 p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

const ROTAS_PUBLICAS = ["/login", "/register", "/checkout", "/obrigado", "/termos", "/privacidade", "/redefinir-senha", "/confirmar-email", "/convite", "/assinatura"];

// "/" e a pagina de vendas para quem nao esta logado e o painel para quem esta.
function Inicio() {
  const { user, loading } = useAuth();
  if (user) {
    return (
      <ProtectedRoute>
        <AppLayout><Dashboard /></AppLayout>
      </ProtectedRoute>
    );
  }
  if (loading && localStorage.getItem("auth_token")) {
    return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
  }
  return <Landing />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // Show loading ONLY for protected routes, not for the public pages
  const location = window.location.pathname;
  const isPublicRoute = location === "/" || ROTAS_PUBLICAS.includes(location);

  if (loading && !isPublicRoute) {
    return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
  }

  return (
    <Routes>
      <Route path="/" element={<Inicio />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/checkout" element={<CheckoutRedirect />} />
      <Route path="/obrigado" element={<ObrigadoPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/termos" element={<TermosPage />} />
      <Route path="/privacidade" element={<PrivacidadePage />} />
      <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
      <Route path="/convite" element={<ConvitePage />} />
      <Route path="/confirmar-email" element={<ConfirmarEmailPage />} />
      <Route path="/assinatura" element={<SubscriptionPage />} />
      <Route path="*" element={
        <ProtectedRoute>
          <AppLayout>
            <Routes>
              <Route path="/products" element={<Products />} />
              <Route path="/technical-sheets" element={<TechnicalSheets />} />
              <Route path="/technical-sheets/new" element={<TechnicalSheetForm />} />
              <Route path="/technical-sheets/:id" element={<TechnicalSheetForm />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/templates/new" element={<TemplateForm />} />
              <Route path="/templates/:id" element={<TemplateForm />} />
              <Route path="/cost-sheets" element={<CostSheets />} />
              <Route path="/cost-sheets/new" element={<CostSheetForm />} />
              <Route path="/cost-sheets/:id" element={<CostSheetForm />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/config" element={<ConfigPage />} />
              <Route path="/conta" element={<AccountPage />} />
              <Route path="/admin" element={user?.role === "admin" ? <AdminPage /> : <Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
